import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CreditCard, Loader2 } from 'lucide-react';
import { authHelper } from '../utils/authHelper';

export function CheckoutPage({ packageId, packageName, price }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleCheckout = async () => {
    setLoading(true);
    setError('');

    try {
      const originUrl = window.location.origin;
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: authHelper.getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          package_id: packageId,
          origin_url: originUrl
        })
      });

      if (response.status === 401) {
        navigate('/login');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      
      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#02040A] flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-gradient-to-b from-white/10 to-transparent backdrop-blur-xl border border-white/20 rounded-3xl p-8 shadow-2xl"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#C5A14C]/20 flex items-center justify-center">
            <CreditCard className="w-8 h-8 text-[#C5A14C]" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">{packageName}</h2>
          <p className="text-5xl font-black text-[#C5A14C] mb-4">${price}</p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <button
          onClick={handleCheckout}
          disabled={loading}
          className="w-full bg-[#C5A14C] text-[#02040A] font-bold rounded-full px-8 py-4 hover:bg-[#B08F3C] hover:shadow-[0_0_20px_rgba(197,161,76,0.4)] transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Procesando...
            </>
          ) : (
            'Proceder al Pago'
          )}
        </button>

        {/* Política de Devolución */}
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 mt-4">
          <p className="text-xs font-semibold text-amber-400 mb-1">
            📋 Política de Devolución
          </p>
          <p className="text-xs text-amber-300/80">
            Todas las ventas son finales. No se realizan devoluciones ni reembolsos.
          </p>
        </div>

        <p className="text-xs text-white/50 text-center mt-4">
          Serás redirigido a Stripe para completar el pago de forma segura
        </p>
      </motion.div>
    </div>
  );
}
